/**
 * What the calorie ring should draw.
 *
 * The arithmetic lives here rather than in each client's `Ring` component
 * because it was written twice, and the two copies are exactly the kind of
 * thing that drifts: a fix applied to the web's ring and not the phone's makes
 * the same day read differently on two screens.
 *
 * From the spec: *"Ring is calories remaining, split into protein/carbs/fat
 * arcs for what has been eaten."* So the full circle is the day's target, the
 * drawn arcs are what has been eaten broken down by macro, and the gap left is
 * the headline number in the middle.
 */

import { KCAL_PER_GRAM, type Macros } from "./macros";

export type MacroKey = "protein" | "carbs" | "fat";

export interface RingArc {
  macro: MacroKey;
  /** Fraction of the full circle, 0–1. */
  length: number;
  /** Where the arc begins, as a fraction of the circle. */
  start: number;
}

export interface RingModel {
  /** Empty when there is no target — there is no circle to fill. */
  arcs: RingArc[];
  /** The big number in the middle. Never negative; `label` carries the sense. */
  headline: number;
  label: "kcal left" | "kcal over" | "kcal eaten";
  over: boolean;
  /** The "1,450 / 2,000" line. Null without a target. */
  ratio: { eaten: number; target: number } | null;
}

const SEGMENTS: { macro: MacroKey; key: keyof Macros; kcalPerGram: number }[] = [
  { macro: "protein", key: "protein_g", kcalPerGram: KCAL_PER_GRAM.protein },
  { macro: "carbs", key: "carbs_g", kcalPerGram: KCAL_PER_GRAM.carbs },
  { macro: "fat", key: "fat_g", kcalPerGram: KCAL_PER_GRAM.fat },
];

export function ringModel(consumed: Macros, targetKcal: number | null): RingModel {
  const eaten = consumed.kcal;
  const target = targetKcal ?? 0;

  // No target: report the intake plainly rather than inventing a denominator.
  //
  // This used to fall through to the "kcal left" branch, where `target - eaten`
  // is negative and gets an abs() — so a day with no goal and 1,800 kcal logged
  // announced "1,800 kcal left", which is not merely unhelpful but the exact
  // opposite of the truth, in the largest type on the screen.
  if (target <= 0) {
    return { arcs: [], headline: eaten, label: "kcal eaten", over: false, ratio: null };
  }

  const over = eaten > target;
  if (over) {
    // One alert-coloured ring says the thing that matters. Keeping the macro
    // split here would bury the headline in detail.
    return {
      arcs: [],
      headline: eaten - target,
      label: "kcal over",
      over: true,
      ratio: { eaten, target },
    };
  }

  // Lay the arcs end to end. Each is a fraction of the *target*, so the gap at
  // the end is genuinely the calories left.
  //
  // Arc lengths come from each macro's energy contribution, not its grams: nine
  // grams of fat and nine of carbs are very different slices of a calorie
  // budget, so equal-length arcs would misdescribe the day.
  let offset = 0;
  const arcs: RingArc[] = [];
  for (const { macro, key, kcalPerGram } of SEGMENTS) {
    // Clamped to the space left, not just to the circle. The macro grams and
    // the stated kcal disagree often enough on crowd-sourced data that the
    // arcs can sum past a total that is itself under target — see the note on
    // KCAL_PER_GRAM. Unclamped, the last arc wraps back over the first.
    const length = Math.min((consumed[key] * kcalPerGram) / target, 1 - offset);
    arcs.push({ macro, length: Math.max(length, 0), start: offset });
    offset = Math.min(offset + Math.max(length, 0), 1);
  }

  return { arcs, headline: target - eaten, label: "kcal left", over: false, ratio: { eaten, target } };
}
